import { useEffect, useRef } from 'react';

/**
 * project_management#507 — keep a recording list fresh while any row on it is
 * being (re)analysed, so the "Analysed" date and the status visibly change
 * when the result lands (10–25 s per recording; longer when a reanalysis
 * batch holds the queue). Without this the row sat at "Analysing…" until a
 * manual reload, and a finished reanalysis looked like a lost one.
 *
 * Polls only while `inFlight` is true and gives up after `maxMs`: a lost
 * request leaves `isBeingAnalysed` true until the backend watchdog clears it
 * (30 min), and a forgotten tab must not poll for that long.
 */
export const ANALYSIS_POLL_INTERVAL_MS = 5_000;
export const ANALYSIS_POLL_MAX_MS = 10 * 60_000;

export function useRefreshWhileAnalysing(
  inFlight: boolean,
  refresh: () => unknown,
  { intervalMs = ANALYSIS_POLL_INTERVAL_MS, maxMs = ANALYSIS_POLL_MAX_MS } = {},
): void {
  // Latest refresh without restarting the timer on every render.
  const refreshRef = useRef(refresh);

  refreshRef.current = refresh;

  useEffect(() => {
    if (!inFlight) return undefined;

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt >= maxMs) {
        clearInterval(timer);

        return;
      }

      refreshRef.current();
    }, intervalMs);

    return () => clearInterval(timer);
  }, [inFlight, intervalMs, maxMs]);
}
